import { capitalize } from "lodash";
import { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import { HttpTransportType, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { viewPhoneFromFlat } from "@shared/models/Communications/viewPhone";
import phoneDncStatusApi from "@shared/phoneDncStatusApi";
import Avatar from "@shared/v2/Avatar";
import Button from "@shared/v2/Button";
import FieldLabel from "@shared/v2/FieldLabel";
import { Popover, PopoverContent, PopoverItem, PopoverTrigger } from "@shared/v2/Popover";
import SmsComposerInput from "@shared/SmsComposerInput";
import TextLink from "@shared/v2/TextLink";
import Tooltip from "@shared/v2/Tooltip";
import Alert from "@shared/v2/Alert";
import { SMS_STATUS_ICON_MODIFIERS } from "@shared/models/Communications/viewPhoneIcons/SmsStatusIcon";
import EmptySmsHistory from "@shared/EmptySmsHistory";
import { CaretDownSolidV6, SparklesSolidV6, SpinnerSolidV6 } from "@shared/v2/Icomoon";
import { DncFlag, LastSeen, placeholderContent } from "@shared/Sms";
import AiSummary from "@shared/AiAssistant/AiSummary";
import { getAiToken, getExternalApi } from "@shared/externalApi";
import MmsMediaModal from "../../shared/MmsMediaModal";
import ConversationMessage from "../../Inbox/components/ConversationMessage";
import { transformToTimelineSmsEvent } from "../../Inbox/helpers/transformSms";
import { transformConversation } from "../../reducers/messagingReducer";
import * as apiActions from "../../LeadIndex/api";
import { noNamer } from "../../Contacts/ModalViews/helpers";
import Loading from "../../Goals/components/Loader";
import AvatarCluster from "../../Inbox/components/AvatarCluster";

const DEFAULT_ERROR =
  "There was a problem loading SMS messages. Please check your connection and try again or contact support by calling 855-427-4848";

const normalizePhoneNumber = (phoneNumber) => (phoneNumber || "").replace(/\D/g, "").slice(-10);

const getConversationTimestamp = (conversation) => {
  const messageTimestamp =
    conversation?.mostRecentMessage?.dateUpdated || conversation?.mostRecentMessage?.dateCreated;

  return messageTimestamp ? dayjs(messageTimestamp).fromNow() : null;
};

const buildPhoneNumberOption = ({ phoneNumber, conversation = null, phoneDetail = null }) => {
  const viewPhone = phoneDetail
    ? viewPhoneFromFlat(phoneDetail)
    : { value: phoneNumber, formattedNumber: normalizePhoneNumber(phoneNumber) };
  const title =
    conversation?.conversationType === "groupSms" && conversation?.friendlyName
      ? conversation.friendlyName
      : viewPhone.formattedNumber;

  return {
    title,
    viewPhone,
    id: conversation?.uuid || viewPhone.formattedNumber,
    conversation,
  };
};

class SmsConversation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      conversation: null,
      // This defaultHomeAppTabContent prop is currently being set from the Home App card and is also used in PersonInteractionEmailForm.
      // If we allow other areas of the app to set default email/text content then we should rename this to be more generic. Sending a text with this prop set
      // updates  the person's home_app_invite_sent (called in touchHomeAppInviteSent on line 164 below) so we will need to also include a prop
      // signifying where the default content is being set as we would not want make this update
      newSmsMessage: props.defaultHomeAppTabContent || "",
      newSmsMediaFile: null,
      sendingMessage: false,
      fetchingMessages: false,
      smsMessages: [],
      conversationLoaded: false,
      moreSmsToLoad: true,
      messageForMmsMediaModal: null,
      mediaIndexForMmsMediaModal: null,
      errors: [],
      phoneNumbers: [],
      groupPhoneNumbers: [],
      selectedPhoneNumber: null,
      dropdownOpen: false,
      page: 1,
      perPage: 25,
      currentUser: props.currentUser,
    };
  }

  componentDidMount() {
    this.getMutalConversations();
    this.setupSubscription();
  }

  componentDidUpdate(_, prevState) {
    const { conversation } = this.state;
    const { currentUser } = this.props;
    if (
      conversation?.isTrackingNumber &&
      conversation?.isTrackingNumber !== prevState.conversation?.isTrackingNumber
    ) {
      const conversationUsers = conversation?.conversationUsers;
      const teamName = currentUser.account?.teamName;
      const agentname = currentUser.fullName;
      const trackingNumber = conversationUsers?.find((user) => user.isConversationOwner)?.address || "";

      this.setState({
        newSmsMessage: `Hi, this is ${agentname} with the ${teamName}. I saw your message to ${trackingNumber}, and I wanted to respond from my direct work number for easier communication. `,
      });
    }
  }

  componentWillUnmount() {
    this.connection?.stop().catch(console.log);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!prevState.setHomeApp && nextProps.defaultHomeAppTabContent) {
      return { newSmsMessage: nextProps.defaultHomeAppTabContent, setHomeApp: true };
    }
    return null;
  }

  handleChange(value) {
    this.setState({ newSmsMessage: value });
  }

  getConversationPhoneNumber = (conversation) => {
    const { person } = this.props;
    const personUuid = person?.data?.attributes?.uuid;

    return (
      conversation?.conversationUsers?.find(
        (user) => !user.isConversationOwner && user.personUuid === personUuid,
      )?.address || null
    );
  };

  getMutalConversations() {
    const { person, messagingServiceUrl } = this.props;
    const messagingApi = getExternalApi(messagingServiceUrl);
    const phoneDetails = person?.data?.attributes?.phone_details || [];
    const dncPromise = phoneDetails.length
      ? phoneDncStatusApi
          .index(phoneDetails.map((phoneDetail) => phoneDetail.value))
          .then(({ data }) => data)
          .catch(() => ({}))
      : Promise.resolve({});

    Promise.all([messagingApi.get(`/Conversation/Mutual/${person.data.attributes.uuid}`), dncPromise])
      .then(([{ data }, dncStatuses]) => {
        const phoneDetailsByNumber = phoneDetails.reduce((details, phoneDetail) => {
          // eslint-disable-next-line no-param-reassign
          details[normalizePhoneNumber(phoneDetail.canonical_value || phoneDetail.value)] = {
            ...phoneDetail,
            dnc: phoneDncStatusApi.isDnc(phoneDetail.value, dncStatuses),
          };
          return details;
        }, {});

        const buildConversationOptions = (conversationType) =>
          (data?.[conversationType] || []).flatMap((pageData) =>
            (pageData?.conversations || [])
              .map((conversation) => {
                const phoneNumber = this.getConversationPhoneNumber(conversation);

                if (!phoneNumber) return null;

                return buildPhoneNumberOption({
                  phoneNumber,
                  conversation,
                  phoneDetail: phoneDetailsByNumber[normalizePhoneNumber(phoneNumber)],
                });
              })
              .filter(Boolean),
          );

        const smsPhoneNumbers = buildConversationOptions("Sms");
        const existingSmsPhoneNumbers = new Set(
          smsPhoneNumbers.map((phone) => normalizePhoneNumber(phone.viewPhone.formattedNumber)),
        );
        const fallbackPhoneNumbers = this.buildFallbackPhoneNumbers(existingSmsPhoneNumbers);
        const groupPhoneNumbers = buildConversationOptions("GroupSms");
        const phoneNumbers = [...smsPhoneNumbers, ...fallbackPhoneNumbers];

        const selectedPhoneNumber = phoneNumbers.find((p) => p.viewPhone.isPrimary) || phoneNumbers[0];

        this.setState(
          {
            phoneNumbers,
            groupPhoneNumbers,
            selectedPhoneNumber,
            dropdownOpen: false,
            conversationLoaded: phoneNumbers.length === 0,
          },
          () => {
            if (selectedPhoneNumber) {
              this.getSmsMessages(() => {
                this.scrollToBottom();
              });
            }
          },
        );
      })
      .catch(() => {
        const fallbackPhoneNumbers = this.buildFallbackPhoneNumbers();
        const selectedPhoneNumber = fallbackPhoneNumbers[0];

        this.setState(
          {
            phoneNumbers: fallbackPhoneNumbers,
            selectedPhoneNumber,
            dropdownOpen: false,
            conversationLoaded: fallbackPhoneNumbers.length === 0,
          },
          () => {
            if (selectedPhoneNumber) {
              this.getSmsMessages(() => {
                this.scrollToBottom();
              });
            }
          },
        );
      });
  }

  buildFallbackPhoneNumbers = (excludedPhoneNumbers = new Set()) => {
    const { person } = this.props;
    const phoneDetails = person?.data?.attributes?.phone_details || [];

    return phoneDetails
      .filter((phoneDetail) => !excludedPhoneNumbers.has(normalizePhoneNumber(phoneDetail.canonical_value)))
      .map((phoneDetail) =>
        buildPhoneNumberOption({
          phoneNumber: phoneDetail.value,
          phoneDetail,
        }),
      );
  };

  selectPhoneNumber = (selectedPhoneNumber) => {
    this.setState(
      {
        conversation: selectedPhoneNumber?.conversation || null,
        smsMessages: [],
        moreSmsToLoad: true,
        page: 1,
        selectedPhoneNumber,
        dropdownOpen: false,
      },
      () => {
        this.getSmsMessages(() => {
          this.scrollToBottom();
        });
      },
    );
  };

  scrollToBottom = () => {
    this.smsConversationWrapper.scrollTop = this.smsConversationWrapper.scrollHeight;
  };

  clearForm = () => {
    this.setState({
      newSmsMessage: "",
      newSmsMediaFile: null,
      errors: [],
    });
  };

  setMediaFile = (file) => {
    this.setState({ newSmsMediaFile: file || null });
  };

  getSmsMessages = (callback) => {
    const { smsMessages, moreSmsToLoad, fetchingMessages, conversation, page, perPage, selectedPhoneNumber } =
      this.state;
    const { person, messagingServiceUrl, selectedMessageUUID } = this.props;
    const messagingApi = getExternalApi(messagingServiceUrl);

    let conversationPromise;

    if (!fetchingMessages && moreSmsToLoad) {
      this.setState({ fetchingMessages: true });

      if (conversation) {
        conversationPromise = Promise.resolve(conversation);
      } else if (selectedMessageUUID) {
        conversationPromise = messagingApi
          .get(`/Conversation/UnclaimedTrackingNumber/${selectedMessageUUID}`)
          .then(({ data }) => transformConversation(data))
          .catch((err) => {
            if (err?.response?.status === 409 && err?.response?.data) {
              return {
                data: {
                  ...err.response.data,
                  isConflict: true,
                },
              };
            }
            throw err;
          });
      } else if (selectedPhoneNumber) {
        conversationPromise = messagingApi
          .post(`/Conversation/WithUsers`, {
            conversationType: "groupSms",
            isChannel: false,
            users: [
              {
                address: selectedPhoneNumber?.viewPhone.formattedNumber,
                personUuid: person.data?.attributes?.uuid,
                isConversationOwner: false,
              },
            ],
          })
          .catch((err) => {
            if (err?.response?.status === 409 && err?.response?.data) {
              return {
                data: {
                  ...err.response.data,
                  isConflict: true,
                },
              };
            }
            throw err;
          })
          .then(({ data }) => transformConversation(data));
      }

      if (!conversationPromise) return;

      conversationPromise
        .then((c) => {
          this.setState({ conversation: c });
          if (!c.uuid || c.isConflict) {
            this.setState({
              conversationLoaded: true,
              fetchingMessages: false,
            });
            return;
          }
          messagingApi
            .get(`/ConversationMessages/${c.uuid}/${page}/${perPage}`)
            .then((res) => {
              const newSmsMessages = res.data.items.reverse();
              this.setState(
                {
                  smsMessages: [...newSmsMessages, ...smsMessages],
                  moreSmsToLoad: newSmsMessages.length === 25,
                  errors: [],
                  fetchingMessages: false,
                  conversationLoaded: true,
                  page: page + 1,
                },
                () => {
                  callback?.();
                },
              );
            })
            .catch((err) => {
              this.setState({
                errors: [err?.response?.data || DEFAULT_ERROR],
                fetchingMessages: false,
              });
            });
        })
        .catch((err) => {
          let errors =
            (typeof err?.response?.data === "string" ? err.response.data : err?.response?.data?.message) ||
            DEFAULT_ERROR;

          if (err?.response?.status === 422) {
            errors = (
              <div>
                You need a Brivity number to text contacts.{" "}
                <a href="/account/phone_settings" className="tw-font-bold">
                  Set one up
                </a>{" "}
                to continue.
              </div>
            );
          }
          this.setState({
            errors: [errors],
            fetchingMessages: false,
          });
        });
    }
  };

  addMessage = (newMsg) => {
    const { smsMessages, conversation } = this.state;
    const { addSmsToTimeline } = this.props;

    if (smsMessages.find((msg) => msg.uuid === newMsg.uuid)) return;

    this.setState(
      (state) => ({
        smsMessages: [...state.smsMessages, newMsg],
      }),
      () => {
        addSmsToTimeline?.(transformToTimelineSmsEvent(conversation, newMsg));
        this.scrollToBottom();
        this.markAsRead();
      },
    );
  };

  setupSubscription = () => {
    const { messagingServiceUrl } = this.props;
    getAiToken().then(({ token }) => {
      this.connection = new HubConnectionBuilder()
        .withUrl(`${messagingServiceUrl}/MessageHub?access_token=${token}`, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
          withCredentials: true,
        })
        .withAutomaticReconnect()
        .configureLogging(process.env.NODE_ENV === "production" ? LogLevel.Error : LogLevel.Debug)
        .build();

      this.connection.on("ActivateConversation", (conversationUuid) => {
        this.connection.send("AddPersonToGroup", conversationUuid);
      });

      this.connection.on("CreateConversation", (conversation) => {
        this.connection.send("AddPersonToGroup", conversation.uuid);
      });

      this.connection.on("NewMessage", (message) => {
        const { conversation } = this.state;
        if (message.conversationUuid === conversation?.uuid) this.addMessage(message);
      });

      this.connection.on("UpdateSmsOrGroupSmsConversationUserMessageStatusOnFailure", (deliveryStatus) => {
        const { messages } = this.state;
        const message = messages.find((o) => o.uuid === deliveryStatus.messageUuid);
        if (message?.deliveryStatuses) {
          message.deliveryStatuses = message.deliveryStatuses.map((s) =>
            s.conversationUserUuid === deliveryStatus.conversationUserUuid ? deliveryStatus : s,
          );
        }
      });

      this.connection.start().catch(console.log);
    });
  };

  markAsRead = () => {
    const { conversation } = this.state;
    const { messagingServiceUrl } = this.props;
    const messagingApi = getExternalApi(messagingServiceUrl);
    messagingApi.put(`/ConversationUser/ResetUnreadCount/${conversation?.uuid}`).catch(console.log);
  };

  sendSmsMessage = () => {
    const { personId, messagingServiceUrl, defaultHomeAppTabContent, touchHomeAppInviteSent, listingId } =
      this.props;
    const { newSmsMessage, newSmsMediaFile, conversation } = this.state;
    const messagingApi = getExternalApi(messagingServiceUrl);
    const appInviteNeedsUpdating = !!defaultHomeAppTabContent;

    if (!conversation) return;

    this.setState({ sendingMessage: true }, () => {
      const formData = new FormData();
      formData.append("conversationUuid", conversation.uuid);
      formData.append("messageType", "standard");
      if (newSmsMessage) formData.append("message", newSmsMessage);
      if (newSmsMediaFile) formData.append("attachment", newSmsMediaFile);
      if (listingId) formData.append("attributes", JSON.stringify([{ key: "listing_id", value: listingId }]));

      return messagingApi
        .post("/Message/Sms", formData)
        .then(({ data: message }) => {
          this.clearForm();
          this.setState({ sendingMessage: false });
          if (appInviteNeedsUpdating) {
            touchHomeAppInviteSent(personId);
          }

          if (message.conversation) {
            this.setState({
              conversation: {
                ...message.conversation,
                conversationOwnerPersonUuids:
                  message.conversation?.conversationOwnerPersonUuids ||
                  (message.conversation.conversationUsers || [])
                    .filter((u) => u?.isConversationOwner)
                    .map((u) => u?.personUuid)
                    .filter(Boolean),
              },
            });
          }
        })
        .catch((err) => {
          console.log(err);
          this.setState({
            sendingMessage: false,
            // TODO: Catalog different error response types and create a helper to decode them
            errors: [
              typeof err?.response?.data === "string"
                ? err.response.data
                : err?.response?.data?.errors?.Message?.[0] || DEFAULT_ERROR,
            ],
          });
        });
    });
  };

  openMmsMediaModal = (message, index) => {
    this.setState({
      messageForMmsMediaModal: message,
      mediaIndexForMmsMediaModal: index,
    });
  };

  closeMmsMediaModal = () => {
    this.setState({
      messageForMmsMediaModal: null,
      mediaIndexForMmsMediaModal: null,
    });
  };

  closeOrClearBtn = () => {
    const { selectedPhoneNumber } = this.state;
    const { closeModal } = this.props;
    const { clearForm } = this;
    const phone = selectedPhoneNumber?.viewPhone;
    const messageable = !phone || !phone.smsUnsubscribed;
    const callback = closeModal || clearForm;
    const text = closeModal ? "Close" : "Clear";

    return (
      <Button schema="tertiary" onClick={callback} size="medium" disabled={!messageable}>
        {text}
      </Button>
    );
  };

  handleScroll = (e) => {
    const { moreSmsToLoad } = this.state;
    if (moreSmsToLoad && e.target.scrollTop === 0) {
      const currentScrollHeight = e.target.scrollHeight;
      this.getSmsMessages(() => {
        this.smsConversationWrapper.scrollTop =
          this.smsConversationWrapper.scrollHeight - currentScrollHeight - 80;
      });
    }
  };

  render() {
    const {
      conversation,
      conversationLoaded,
      currentUser,
      dropdownOpen,
      errors,
      fetchingMessages,
      groupPhoneNumbers,
      mediaIndexForMmsMediaModal,
      messageForMmsMediaModal,
      newSmsMediaFile,
      newSmsMessage,
      phoneNumbers,
      sendingMessage,
      selectedPhoneNumber,
      smsMessages,
    } = this.state;

    const { person, closeModal, featureFlags } = this.props;
    const conflictUser = conversation?.conversationUsers?.find((u) => !u.isConversationOwner);
    const phone = selectedPhoneNumber?.viewPhone;
    const placeholder = placeholderContent(phone);
    const messageable = (!phone || !phone.smsUnsubscribed) && conversation && !conversation.isConflict;

    return (
      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        <div className="tw-flex tw-justify-between tw-items-center tw-gap-[16px]">
          <div className="tw-flex tw-items-center tw-gap-[8px]">
            <FieldLabel label="To: " />
            {!conversationLoaded && currentUser.managedNumber && <Loading />}
            <Popover
              open={dropdownOpen}
              onOpenChange={(open) => this.setState({ dropdownOpen: open })}
              placement="bottom-start"
              forceTriggerWidth
              maxHeight={320}
            >
              <PopoverTrigger>
                <button
                  type="button"
                  onClick={() => this.setState((state) => ({ dropdownOpen: !state.dropdownOpen }))}
                  className={`tw-w-full tw-border-0 tw-min-w-[100px] tw-bg-white tw-p-[4px] tw-text-left tw-transition-colors ${dropdownOpen ? "tw-border-gray-50" : "tw-border-gray-15 hover:tw-border-gray-30"}`}
                >
                  <div className="tw-text-12d tw-font-semibold tw-flex tw-items-center tw-justify-between tw-gap-[12px]">
                    <div
                      className={`tw-flex tw-items-center tw-gap-[6px] ${SMS_STATUS_ICON_MODIFIERS[phone?.smsStatus || "valid"]}`}
                    >
                      {selectedPhoneNumber?.viewPhone.smsIcon?.icon({})}
                      {capitalize(selectedPhoneNumber?.viewPhone.category)}
                      {selectedPhoneNumber?.viewPhone.isPrimary ? "*" : ""} - {selectedPhoneNumber?.title}
                      <DncFlag small show={selectedPhoneNumber?.viewPhone.dnc} />
                    </div>
                    <span className={`tw-transition-transform ${dropdownOpen ? "tw-rotate-180" : ""}`}>
                      <CaretDownSolidV6 />
                    </span>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="!tw-w-full !tw-max-w-[460px] tw-border-solid tw-border-[1px] tw-border-gray-10">
                {phoneNumbers.map((option) => {
                  const conversationTimestamp = getConversationTimestamp(option.conversation);
                  return (
                    <PopoverItem
                      key={option.id}
                      className={`tw-py-[6px] !tw-h-auto !tw-border-solid tw-border-[1px] tw-border-gray-10 tw-border-x-0 tw-border-t-0 ${option.id === selectedPhoneNumber?.id ? "!tw-bg-gray-5" : ""}`}
                      onClick={() => this.selectPhoneNumber(option)}
                    >
                      <div className="tw-text-12d tw-flex-1 tw-flex tw-justify-between tw-items-center tw-gap-[6px]">
                        <div
                          className={`tw-font-semibold tw-flex tw-items-center tw-gap-[6px] ${SMS_STATUS_ICON_MODIFIERS[option.viewPhone.smsStatus || "valid"]}`}
                        >
                          {option.viewPhone.smsIcon?.icon({})}
                          {capitalize(option.viewPhone.category)}
                          {option.viewPhone.isPrimary ? "*" : ""} - {option.title}
                          <DncFlag small show={option.viewPhone.dnc} />
                        </div>
                        <div className="tw-text-12d tw-text-gray-50 tw-flex tw-items-center tw-gap-[6px]">
                          {conversationTimestamp ? (
                            <div className="tw-text-nowrap">Last message: {conversationTimestamp}</div>
                          ) : (
                            <div className="tw-text-nowrap">No messages</div>
                          )}
                          {option.conversation?.isAiControlled && (
                            <SparklesSolidV6 className="tw-text-semantic-purple-100" />
                          )}
                        </div>
                      </div>
                    </PopoverItem>
                  );
                })}

                {groupPhoneNumbers.length > 0 && (
                  <div className="tw-px-[12px] tw-pt-[16px] tw-pb-[2px] tw-text-12d tw-font-semibold">
                    Group Texts
                  </div>
                )}

                {groupPhoneNumbers.map((option) => {
                  const conversationTimestamp = getConversationTimestamp(option.conversation);
                  return (
                    <PopoverItem
                      key={option.id}
                      className={`!tw-h-auto !tw-border-solid tw-border-[1px] tw-border-gray-10 tw-border-x-0 tw-border-b-0 ${option.id === selectedPhoneNumber?.id ? "!tw-bg-gray-5" : ""}`}
                      onClick={() => this.selectPhoneNumber(option)}
                    >
                      <div className="tw-flex-1 tw-flex tw-justify-between tw-items-center tw-gap-[6px]">
                        <div className="tw-flex tw-items-center tw-gap-[8px]">
                          <div className="tw-flex-none tw-pb-[4px]">
                            <AvatarCluster>
                              {option.conversation.isAiControlled && (
                                <Tooltip
                                  content="AI is applied to this lead."
                                  innerClassName="tw-max-w-[100px]"
                                  trigger={
                                    <div className="tw-bg-semantic-purple-5 tw-w-[24px] tw-h-[24px] tw-border-solid tw-border-semantic-purple-10 tw-rounded-full tw-flex-none tw-flex tw-justify-center tw-items-center">
                                      <SparklesSolidV6 className="tw-text-semantic-purple-100" />
                                    </div>
                                  }
                                />
                              )}
                              {option.conversation.conversationUsers.map((u) => (
                                <Avatar key={u.uuid} src={u.avatar} alt={u.alias} />
                              ))}
                            </AvatarCluster>
                          </div>
                          <div className="tw-text-12d tw-flex tw-flex-col">
                            <span className="tw-text-gray-50">
                              Group Text with {option.conversation.conversationUsers.length} people
                            </span>
                            <span className="tw-font-semibold">{option.title}</span>
                            {option.conversation.conversationUsers.some(
                              (u) =>
                                u.addressStatus?.sms_account_unsubscribed ||
                                u.addressStatus?.sms_soft_unsubscribed,
                            ) && (
                              <span className="tw-text-semantic-red-100">*Some members have opted out</span>
                            )}
                            {option.conversation.conversationUsers.some(
                              (u) => u.addressStatus && !u.addressStatus.sms_textable,
                            ) && (
                              <span className="tw-text-semantic-yellow-100">
                                *Some members have invalid numbers
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="tw-text-12d tw-text-gray-50">
                          {conversationTimestamp ? (
                            <>
                              <div className="tw-text-nowrap">Last message</div>
                              <div className="tw-text-nowrap">{conversationTimestamp}</div>
                            </>
                          ) : (
                            <div className="tw-text-nowrap">No messages</div>
                          )}
                        </span>
                      </div>
                    </PopoverItem>
                  );
                })}
              </PopoverContent>
            </Popover>
          </div>
          <LastSeen smsHistory={smsMessages || []} />
        </div>
        <div
          className="tw-flex tw-flex-col tw-gap-[16px] tw-overflow-auto tw-min-h-[240px] tw-max-h-[330px] tw-border-solid tw-pb-[8px] tw-border-y-1 tw-border-x-0 tw-border-neutral-gray-10"
          ref={(component) => {
            this.smsConversationWrapper = component;
          }}
          onScroll={(e) => this.handleScroll(e)}
        >
          {conversation?.isConflict && (
            <div className="tw-my-[16px]">
              <Alert
                variant="warning"
                title="This conversation already exists"
                text={
                  <div className="tw-flex tw-justify-between tw-items-end">
                    <span>
                      {noNamer(person.data.attributes.first_name, person.data.attributes.last_name)} shares a
                      number with contact {conflictUser.alias}, who you already have an open conversation
                      with.
                    </span>
                    <TextLink
                      href={`/people/${conflictUser.slug}?tab=3`}
                      className="!tw-text-inherit tw-font-semibold tw-uppercase tw-whitespace-nowrap"
                    >
                      Go To Contact
                    </TextLink>
                  </div>
                }
              />
            </div>
          )}
          {fetchingMessages && (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-[8px] tw-py-[16px] tw-text-gray">
              <SpinnerSolidV6 className="tw-animate-spin" size="l" /> Loading message history...
            </div>
          )}
          <EmptySmsHistory
            containerClassName="tw-flex-grow"
            show={!conversation?.isConflict && conversationLoaded && smsMessages.length === 0}
          />
          {!conversation?.isConflict &&
            smsMessages.map((message, index) => (
              <ConversationMessage
                key={message.uuid}
                message={message}
                conversation={conversation}
                onClickImage={this.openMmsMediaModal}
                showTimeAgo={
                  index === 0 ||
                  dayjs(message.dateCreated).valueOf() -
                    dayjs(smsMessages[index - 1]?.dateCreated).valueOf() >
                    7200000
                }
              />
            ))}
        </div>

        {featureFlags.gabbiAi && closeModal && (
          <AiSummary leadPersonUuid={person.data.attributes.uuid} allowAddAI={false} variant="inline" />
        )}

        {errors.map((e) => (
          <Alert variant="error" text={e} />
        ))}
        <SmsComposerInput
          messages={smsMessages}
          aiAssistantContactUuid={person.data.attributes.uuid}
          conversationOwnerPersonUuids={conversation?.conversationOwnerPersonUuids}
          id="text-message-textarea"
          disabled={!messageable}
          fileValue={newSmsMediaFile}
          value={newSmsMessage}
          onFileChange={this.setMediaFile}
          onChange={(value) => this.handleChange(value)}
          placeholder={placeholder}
          imageUploadDisabled={!messageable || Boolean(newSmsMediaFile)}
        />
        <div className="tw-flex tw-justify-between">
          {this.closeOrClearBtn()}
          <Button
            size="medium"
            schema="primary"
            onClick={this.sendSmsMessage}
            disabled={!messageable}
            isLoading={sendingMessage}
          >
            Send Message
          </Button>
        </div>
        <MmsMediaModal
          onClose={this.closeMmsMediaModal}
          message={messageForMmsMediaModal}
          mediaIndex={mediaIndexForMmsMediaModal}
        />
      </div>
    );
  }
}

export default connect(
  (state) => ({
    currentUser: state.layout.currentUser,
    featureFlags: state.layout.featureFlags,
    messagingServiceUrl: state.layout.apiUrls.messagingServiceUrl,
  }),
  { touchHomeAppInviteSent: apiActions.touchHomeAppInviteSent },
)(SmsConversation);

SmsConversation.propTypes = {
  addSmsToTimeline: PropTypes.func,
  closeModal: PropTypes.func,
  currentUser: PropTypes.shape().isRequired,
  defaultHomeAppTabContent: PropTypes.string,
  featureFlags: PropTypes.shape().isRequired,
  listingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  messagingServiceUrl: PropTypes.string,
  person: PropTypes.shape().isRequired,
  personId: PropTypes.number,
  selectedMessageUUID: PropTypes.string,
  touchHomeAppInviteSent: PropTypes.func,
};

SmsConversation.defaultProps = {
  defaultHomeAppTabContent: null,
  touchHomeAppInviteSent: null,
  addSmsToTimeline: null,
  closeModal: null,
  listingId: null,
  messagingServiceUrl: "",
  selectedMessageUUID: null,
  personId: null,
};
